'use client';

import React, { useEffect, useState } from 'react';

interface Fish {
  id: string;
  y: number;
  duration: number;
  size: number;
  delay: number;
  color: string;
  flipped: boolean;
}

interface Bubble {
  id: string;
  x: number;
  duration: number;
  size: number;
  delay: number;
}

export default function OceanBackground() {
  const [fish, setFish] = useState<Fish[]>([]);
  const [bubbles, setBubbles] = useState<Bubble[]>([]);

  useEffect(() => {
    const fishColors = ['#06b6d4', '#f59e0b', '#10b981', '#8b5cf6', '#ef4444', '#f97316'];
    const newFish: Fish[] = Array.from({ length: 8 }, (_, i) => ({
      id: `fish-bg-${i}`,
      y: 10 + (i * 11),
      duration: 8 + (i * 2.3),
      size: i % 3 === 0 ? 48 : i % 2 === 0 ? 32 : 24,
      delay: -(i * 3.1),
      color: fishColors[i % fishColors.length],
      flipped: i % 2 === 0,
    }));
    setFish(newFish);

    const newBubbles: Bubble[] = Array.from({ length: 12 }, (_, i) => ({
      id: `bubble-bg-${i}`,
      x: 5 + (i * 8),
      duration: 4 + (i * 0.7),
      size: 4 + (i % 4) * 3,
      delay: -(i * 1.2),
    }));
    setBubbles(newBubbles);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Depth gradient layers */}
      <div className="absolute inset-0" style={{
        background: 'linear-gradient(180deg, #061525 0%, #0a1e35 25%, #061020 60%, #020810 100%)'
      }} />

      {/* Caustic light rays */}
      <div className="absolute inset-0 opacity-10">
        {Array.from({ length: 6 }, (_, i) => (
          <div
            key={`ray-${i}`}
            className="absolute top-0 w-px"
            style={{
              left: `${10 + i * 16}%`,
              height: '60%',
              background: 'linear-gradient(180deg, rgba(6,182,212,0.6) 0%, transparent 100%)',
              transform: `rotate(${-15 + i * 6}deg)`,
              transformOrigin: 'top center',
            }}
          />
        ))}
      </div>

      {/* Animated fish */}
      {fish.map((f) => (
        <div
          key={f.id}
          className="absolute animate-fish-swim"
          style={{
            top: `${f.y}%`,
            animationDuration: `${f.duration}s`,
            animationDelay: `${f.delay}s`,
            transform: f.flipped ? 'scaleX(-1)' : 'scaleX(1)',
          }}
        >
          <svg width={f.size} height={f.size * 0.6} viewBox="0 0 48 28" fill="none">
            <ellipse cx="28" cy="14" rx="16" ry="10" fill={f.color} opacity="0.7" />
            <polygon points="2,14 14,4 14,24" fill={f.color} opacity="0.5" />
            <circle cx="38" cy="11" r="2" fill="#0a0f1e" />
          </svg>
        </div>
      ))}

      {/* Bubbles */}
      {bubbles.map((b) => (
        <div
          key={b.id}
          className="absolute rounded-full animate-bubble"
          style={{
            left: `${b.x}%`,
            bottom: '0',
            width: b.size,
            height: b.size,
            animationDuration: `${b.duration}s`,
            animationDelay: `${b.delay}s`,
            background: 'rgba(6,182,212,0.15)',
            border: '1px solid rgba(6,182,212,0.3)',
          }}
        />
      ))}

      {/* Seabed */}
      <div className="absolute bottom-0 left-0 right-0 h-20 opacity-30"
        style={{ background: 'linear-gradient(180deg, transparent 0%, #0a1428 100%)' }}
      />
    </div>
  );
}